import { Link } from "@tanstack/react-router";
import { ArrowUpRight, Download, Truck } from "lucide-react";

import { EmptyCard } from "../../components/empty-card";
import { Section, SectionHead, card, figure, ghostButton, label, orb, tag } from "../../components/section";
import { serviceCardSpan } from "../../components/service-card";
import { PageTitle, SiteHead } from "../../components/site-head";
import { SitePage } from "../../components/site-page";
import { productPath, sitePath, useSiteConfig } from "../../hooks/use-site-config";
import { Skeleton } from "@/components/ui/skeleton";
import { money } from "@/features/payments/utils/format";
import { useShop } from "@/features/shop/hooks/use-shop";
import { ProductCover } from "@/features/shop/modules/product-cover/product-cover";
import type { ShopProduct } from "@/features/shop/types";
import { productTag, stockNote } from "@/features/shop/utils/labels";
import { cn } from "@/lib/utils";
import { m } from "@/paraglide/messages.js";

/** Products the home page has room for; the rest are a click away. */
const VISIBLE = 3;

const grid = "grid gap-3 sm:grid-cols-2 lg:grid-cols-6";

/**
 * One product on the trainer's site, built like a service card: the cover, how it arrives on a
 * tag, its name and line, and the price with the arrow that opens it.
 */
const ProductTile = ({ product, className }: { product: ShopProduct; className?: string }) => {
  const { slug } = useSiteConfig();
  const note = stockNote(product);
  const Arrives = product.kind === "digital" ? Download : Truck;

  return (
    <Link
      to={productPath(slug, product.id)}
      className={cn(
        card,
        "group flex flex-col p-2.5 outline-none focus-visible:ring-2 focus-visible:ring-ring md:p-2.5",
        className,
      )}
    >
      <ProductCover product={product} className="aspect-[4/3] w-full" />
      <span className="flex flex-1 flex-col px-3.5 pt-5 pb-3.5 md:px-4.5 md:pb-4.5">
        <span className={tag}>
          <Arrives aria-hidden className="size-3.5" />
          {productTag(product)}
        </span>
        <b className="mt-4 text-[24px] leading-[1.1] font-medium tracking-[-0.03em]">{product.title}</b>
        {product.summary ? <span className="mt-2 line-clamp-2 text-muted-foreground">{product.summary}</span> : null}
        <span className="mt-auto flex items-end justify-between gap-3 pt-6">
          <span className="min-w-0">
            {note ? <span className={cn(label, product.soldOut ? "" : "text-foreground")}>{note}</span> : null}
            <span
              className={cn(
                figure,
                "mt-1 block text-[36px]",
                product.soldOut && "text-muted-foreground line-through decoration-2",
              )}
            >
              {money(product.priceAmount, product.currency)}
            </span>
          </span>
          <span className={cn(orb, "group-hover:bg-primary group-hover:text-primary-foreground")}>
            <ArrowUpRight
              aria-hidden
              className="size-[18px] transition-transform duration-200 group-hover:rotate-45 motion-reduce:transition-none"
            />
          </span>
        </span>
      </span>
    </Link>
  );
};

/** Product tiles in a grid that always fills its rows, the way the services do. */
export const ProductGrid = ({ products }: { products: ShopProduct[] }) => (
  <div className={grid}>
    {products.map((product, index) => (
      <ProductTile key={product.id} product={product} className={serviceCardSpan(index, products.length)} />
    ))}
  </div>
);

const GridSkeleton = () => (
  <div className={grid}>
    {Array.from({ length: 3 }, (_, index) => (
      <Skeleton key={index} className={cn("h-[460px] rounded-[28px]", serviceCardSpan(index, 3))} />
    ))}
  </div>
);

/** The trainer's shop page: everything on the shelf, starting from what the server read with the page. */
export const Shop = ({ initial }: { initial?: ShopProduct[] }) => {
  const site = useSiteConfig();
  const { products, loading } = useShop(site.organizationId, initial);

  return (
    <SitePage
      head={
        <SiteHead>
          <PageTitle title={m.shop_title()} aside={m.shop_page_aside()} />
        </SiteHead>
      }
    >
      {loading ? (
        <GridSkeleton />
      ) : products.length > 0 ? (
        <Section className="pt-3 md:pt-3">
          <ProductGrid products={products} />
        </Section>
      ) : (
        <EmptyCard title={m.shop_page_empty()} />
      )}
    </SitePage>
  );
};

/**
 * The shop on the trainer's home page, under the services. A trainer with nothing on the shelf
 * gets no section at all.
 */
export const ShopBand = () => {
  const { slug, organizationId } = useSiteConfig();
  const { products } = useShop(organizationId);
  if (products.length === 0) return null;

  return (
    <Section id="shop">
      <SectionHead
        title={m.shop_band_title()}
        aside={
          products.length > VISIBLE ? (
            <Link to={sitePath(slug, "shop")} className={ghostButton}>
              {m.shop_band_all()}
            </Link>
          ) : (
            m.shop_band_aside()
          )
        }
      />
      <ProductGrid products={products.slice(0, VISIBLE)} />
    </Section>
  );
};
